import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    ArrowLeft, Edit2, Trash2, CalendarDays, Clock, MapPin, Video, Globe,
    Users, UserPlus, Camera, Search, CheckCircle2, XCircle, AlertTriangle,
    ExternalLink, Loader2, MoreHorizontal, ChevronDown, Link2,
} from 'lucide-react';
import EventPhotosGrid from '../components/events/EventPhotosGrid';
import FaceReviewPanel from '../components/events/FaceReviewPanel';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'attendees', label: 'Attendees' },
    { key: 'photos', label: 'Photos' },
    { key: 'face_review', label: 'Face Review' },
];

const STATUS_CONFIG = {
    registered: { label: 'Registered', bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-200' },
    confirmed: { label: 'Confirmed', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    attended: { label: 'Attended', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    no_show: { label: 'No Show', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

const EVENT_STATUS_CONFIG = {
    draft: { label: 'Draft', bg: 'bg-stone-100', text: 'text-stone-600' },
    published: { label: 'Published', bg: 'bg-blue-50', text: 'text-blue-700' },
    upcoming: { label: 'Upcoming', bg: 'bg-blue-50', text: 'text-blue-700' },
    active: { label: 'Active', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    completed: { label: 'Completed', bg: 'bg-stone-100', text: 'text-stone-700' },
    cancelled: { label: 'Cancelled', bg: 'bg-red-50', text: 'text-red-600' },
};

// User-selectable statuses shown in the badge dropdown. `upcoming` and
// `active` exist for backwards-compat rendering but aren't offered as
// manual choices.
const EVENT_STATUS_OPTIONS = ['draft', 'published', 'completed', 'cancelled'];

const formatDate = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

/* ---------- Stat Card ---------- */
const StatCard = ({ label, value, icon: Icon, borderColor, iconColor }) => (
    <div className={`bg-white rounded-lg border-2 ${borderColor} p-4 flex flex-col items-center justify-center min-w-0`}>
        <Icon className={`w-5 h-5 mb-1.5 ${iconColor}`} />
        <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{value ?? 0}</p>
        <p className="text-xs text-stone-500 mt-0.5">{label}</p>
    </div>
);

/* ---------- Attendee Status Badge ---------- */
const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.registered;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
            {cfg.label}
        </span>
    );
};

/* ---------- Status Dropdown ---------- */
const StatusDropdown = ({ value, onChange, disabled }) => (
    <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`text-xs font-medium rounded-full px-2 py-1 border cursor-pointer focus:outline-none focus:ring-2 focus:ring-stone-900/20 ${STATUS_CONFIG[value]?.bg || 'bg-stone-100'} ${STATUS_CONFIG[value]?.text || 'text-stone-700'} ${STATUS_CONFIG[value]?.border || 'border-stone-200'}`}
    >
        <option value="registered">Registered</option>
        <option value="confirmed">Confirmed</option>
        <option value="attended">Attended</option>
        <option value="no_show">No Show</option>
    </select>
);

/* ---------- Add Contacts Dialog ---------- */
const AddContactsDialog = ({ open, onOpenChange, onAdd, getAuthHeader }) => {
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState([]);
    const [searching, setSearching] = useState(false);
    const [adding, setAdding] = useState(false);

    const doSearch = useCallback(async (q) => {
        if (!q || q.length < 2) { setResults([]); return; }
        setSearching(true);
        try {
            const res = await axios.get(`${API}/contacts`, {
                params: { search: q, page_size: 20 },
                headers: getAuthHeader(),
            });
            const data = Array.isArray(res.data) ? res.data : res.data.data || [];
            setResults(data);
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
        }
    }, [getAuthHeader]);

    useEffect(() => {
        const timer = setTimeout(() => doSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search, doSearch]);

    useEffect(() => {
        if (!open) { setSearch(''); setResults([]); setSelected([]); }
    }, [open]);

    const toggleSelect = (contact) => {
        setSelected(prev =>
            prev.find(c => c.contact_id === contact.contact_id)
                ? prev.filter(c => c.contact_id !== contact.contact_id)
                : [...prev, contact]
        );
    };

    const handleAdd = async () => {
        if (selected.length === 0) return;
        setAdding(true);
        try {
            await onAdd(selected.map(c => c.contact_id));
            onOpenChange(false);
        } finally {
            setAdding(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent style={{ width: '500px', maxWidth: '95vw', maxHeight: '80vh' }}>
                <DialogHeader>
                    <DialogTitle>Add Contacts to Event</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input
                            type="text"
                            placeholder="Search contacts by name or email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            autoFocus
                        />
                    </div>

                    {selected.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {selected.map(c => (
                                <span key={c.contact_id} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-1 rounded-full">
                                    {c.first_name} {c.last_name}
                                    <button onClick={() => toggleSelect(c)} className="hover:text-red-500">
                                        <XCircle className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="max-h-64 overflow-y-auto border border-stone-100 rounded-lg divide-y divide-stone-100">
                        {searching && (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                            </div>
                        )}
                        {!searching && results.length === 0 && search.length >= 2 && (
                            <div className="text-center py-8 text-sm text-stone-400">No contacts found</div>
                        )}
                        {!searching && search.length < 2 && (
                            <div className="text-center py-8 text-sm text-stone-400">Type at least 2 characters to search</div>
                        )}
                        {!searching && results.map(contact => {
                            const isSelected = selected.some(c => c.contact_id === contact.contact_id);
                            return (
                                <button
                                    key={contact.contact_id}
                                    onClick={() => toggleSelect(contact)}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-stone-50 transition-colors ${isSelected ? 'bg-stone-50' : ''}`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${isSelected ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-600'}`}>
                                        {isSelected ? <CheckCircle2 className="w-4 h-4" /> : (
                                            `${(contact.first_name || '?')[0]}${(contact.last_name || '?')[0]}`
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-stone-800 truncate">
                                            {contact.first_name} {contact.last_name}
                                        </p>
                                        <p className="text-xs text-stone-400 truncate">{contact.email || contact.title || '--'}</p>
                                    </div>
                                    {contact.account_name && (
                                        <span className="text-xs text-stone-400 truncate max-w-[120px]">{contact.account_name}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleAdd} disabled={selected.length === 0 || adding}>
                            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
                            Add {selected.length > 0 ? `${selected.length} Contact${selected.length > 1 ? 's' : ''}` : 'Contacts'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Code-split the editor — it only loads when the user opens the Edit dialog.
const LazyEventEditor = React.lazy(() => import('./EventEditor'));

/* ========== MAIN COMPONENT ========== */
export default function EventDetail() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    // Attendees state
    const [attendees, setAttendees] = useState([]);
    const [attendeesLoading, setAttendeesLoading] = useState(false);
    const [attendeePage, setAttendeePage] = useState(1);
    const [attendeesTotal, setAttendeesTotal] = useState(0);
    const [attendeeSearch, setAttendeeSearch] = useState('');
    const [selectedAttendees, setSelectedAttendees] = useState([]);
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [bulkStatus, setBulkStatus] = useState('');
    const [bulkUpdating, setBulkUpdating] = useState(false);

    // Dialogs
    const [showAddContacts, setShowAddContacts] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [updatingEventStatus, setUpdatingEventStatus] = useState(false);

    const PAGE_SIZE = 50;

    /* ---------- Load Event ---------- */
    const loadEvent = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/events/${eventId}`, { headers: getAuthHeader() });
            setEvent(res.data);
        } catch {
            toast.error('Failed to load event');
        } finally {
            setLoading(false);
        }
    }, [eventId, getAuthHeader]);

    /* ---------- Load Attendees ---------- */
    const loadAttendees = useCallback(async (page = 1) => {
        setAttendeesLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (attendeeSearch) params.search = attendeeSearch;
            const res = await axios.get(`${API}/events/${eventId}/attendees`, {
                params,
                headers: getAuthHeader(),
            });
            const data = res.data;
            if (Array.isArray(data)) {
                setAttendees(data);
                setAttendeesTotal(data.length);
            } else {
                setAttendees(data.data || []);
                setAttendeesTotal(data.total || 0);
            }
        } catch {
            toast.error('Failed to load attendees');
        } finally {
            setAttendeesLoading(false);
        }
    }, [eventId, attendeeSearch, getAuthHeader]);

    useEffect(() => { loadEvent(); }, [loadEvent]);

    useEffect(() => {
        if (activeTab === 'attendees') loadAttendees(attendeePage);
    }, [activeTab, attendeePage, loadAttendees]);

    /* ---------- Add Contacts ---------- */
    const handleAddContacts = async (contactIds) => {
        try {
            await axios.post(`${API}/events/${eventId}/attendees`, { contact_ids: contactIds }, { headers: getAuthHeader() });
            toast.success(`Added ${contactIds.length} contact${contactIds.length > 1 ? 's' : ''}`);
            loadAttendees(attendeePage);
            loadEvent();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add contacts');
            throw err;
        }
    };

    /* ---------- Update Attendee Status ---------- */
    const handleStatusChange = async (attendeeId, newStatus) => {
        setUpdatingStatus(prev => ({ ...prev, [attendeeId]: true }));
        try {
            await axios.put(`${API}/events/${eventId}/attendees/${attendeeId}`, { status: newStatus }, { headers: getAuthHeader() });
            setAttendees(prev => prev.map(a =>
                (a._id === attendeeId || a.id === attendeeId) ? { ...a, status: newStatus } : a
            ));
            loadEvent();
        } catch {
            toast.error('Failed to update status');
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [attendeeId]: false }));
        }
    };

    /* ---------- Bulk Update Status ---------- */
    const handleBulkStatusUpdate = async () => {
        if (!bulkStatus || selectedAttendees.length === 0) return;
        setBulkUpdating(true);
        try {
            await Promise.all(
                selectedAttendees.map(id =>
                    axios.put(`${API}/events/${eventId}/attendees/${id}`, { status: bulkStatus }, { headers: getAuthHeader() })
                )
            );
            toast.success(`Updated ${selectedAttendees.length} attendee${selectedAttendees.length > 1 ? 's' : ''}`);
            setSelectedAttendees([]);
            setBulkStatus('');
            loadAttendees(attendeePage);
            loadEvent();
        } catch {
            toast.error('Failed to update some attendees');
        } finally {
            setBulkUpdating(false);
        }
    };

    /* ---------- Remove Attendee ---------- */
    const handleRemoveAttendee = async (attendeeId) => {
        try {
            await axios.delete(`${API}/events/${eventId}/attendees/${attendeeId}`, { headers: getAuthHeader() });
            toast.success('Attendee removed');
            loadAttendees(attendeePage);
            loadEvent();
        } catch {
            toast.error('Failed to remove attendee');
        }
    };

    /* ---------- Change Event Status ---------- */
    const handleEventStatusChange = async (newStatus) => {
        if (newStatus === event?.status) { setShowStatusMenu(false); return; }
        setUpdatingEventStatus(true);
        try {
            await axios.put(
                `${API}/events/${eventId}`,
                { status: newStatus },
                { headers: getAuthHeader() },
            );
            setEvent(prev => ({ ...prev, status: newStatus }));
            toast.success(`Status → ${EVENT_STATUS_CONFIG[newStatus]?.label || newStatus}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update status');
        } finally {
            setUpdatingEventStatus(false);
            setShowStatusMenu(false);
        }
    };

    /* ---------- Delete Event ---------- */
    const handleDeleteEvent = async () => {
        setDeleting(true);
        try {
            await axios.delete(`${API}/events/${eventId}`, { headers: getAuthHeader() });
            toast.success('Event deleted');
            navigate('/events');
        } catch {
            toast.error('Failed to delete event');
        } finally {
            setDeleting(false);
        }
    };

    /* ---------- Select All Toggle ---------- */
    const toggleSelectAll = () => {
        if (selectedAttendees.length === attendees.length) {
            setSelectedAttendees([]);
        } else {
            setSelectedAttendees(attendees.map(a => a._id || a.id));
        }
    };

    const toggleSelectAttendee = (id) => {
        setSelectedAttendees(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    /* ---------- Loading / Error ---------- */
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!event) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <AlertTriangle className="w-8 h-8 text-stone-400" />
                <p className="text-stone-500">Event not found</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/events')}>
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back to Events
                </Button>
            </div>
        );
    }

    const eventStatus = EVENT_STATUS_CONFIG[event.status] || EVENT_STATUS_CONFIG.draft;
    const stats = event.stats || {};
    const totalPages = Math.ceil(attendeesTotal / PAGE_SIZE);

    /* ========== EDIT MODE — inline on the page ========== */
    if (showEdit) {
        return (
            <div className="space-y-4 max-w-5xl animate-fade-in">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowEdit(false)}
                        className="text-stone-500 hover:text-stone-800 p-1"
                        aria-label="Back to event"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <h1 className="text-lg font-semibold text-stone-800">Edit event</h1>
                </div>
                <React.Suspense fallback={
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                    </div>
                }>
                    <LazyEventEditor
                        eventId={eventId}
                        onCancel={() => setShowEdit(false)}
                        onSave={() => {
                            setShowEdit(false);
                            loadEvent();
                        }}
                    />
                </React.Suspense>
            </div>
        );
    }

    /* ========== RENDER ========== */
    return (
        <div className="space-y-6 max-w-5xl animate-fade-in">
            {/* ---- Header ---- */}
            <div className="flex items-start gap-4">
                <button
                    onClick={() => navigate('/events')}
                    className="mt-1 p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2.5">
                                <h1 className="text-2xl font-bold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {event.name || 'Untitled Event'}
                                </h1>
                                {event.event_number && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold bg-stone-100 text-stone-500 border border-stone-200 shrink-0">
                                        {event.event_number}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                {/* Click-to-change status. Admins + event owner
                                    can switch between Draft / Published /
                                    Completed / Cancelled. */}
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowStatusMenu(v => !v)}
                                        disabled={updatingEventStatus}
                                        data-action="event.change_status"
                                        title="Change status"
                                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${eventStatus.bg} ${eventStatus.text} hover:ring-2 hover:ring-stone-200 disabled:opacity-60`}
                                    >
                                        {updatingEventStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                        {eventStatus.label}
                                        <ChevronDown className="w-3 h-3" />
                                    </button>
                                    {showStatusMenu && (
                                        <div
                                            className="absolute left-0 top-full mt-1 w-40 bg-white border border-stone-200 rounded-lg shadow-lg z-20 py-1"
                                            onMouseLeave={() => setShowStatusMenu(false)}
                                        >
                                            {EVENT_STATUS_OPTIONS.map(s => {
                                                const cfg = EVENT_STATUS_CONFIG[s];
                                                const active = event.status === s;
                                                return (
                                                    <button
                                                        key={s}
                                                        type="button"
                                                        onClick={() => handleEventStatusChange(s)}
                                                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between ${
                                                            active ? 'bg-stone-50' : 'hover:bg-stone-50'
                                                        }`}
                                                    >
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                                                            {cfg.label}
                                                        </span>
                                                        {active && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                {event.event_type && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-50 text-stone-500 border border-stone-200">
                                        {event.event_type}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowEdit(true)}
                                data-action="event.edit"
                            >
                                <Edit2 className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowDeleteConfirm(true)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Tabs ---- */}
            <div className="border-b border-stone-200">
                <nav className="flex gap-6 -mb-px">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.key
                                    ? 'border-stone-900 text-stone-900'
                                    : 'border-transparent text-stone-400 hover:text-stone-600 hover:border-stone-300'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* ---- Tab Content ---- */}
            {activeTab === 'overview' && <OverviewTab event={event} navigate={navigate} />}
            {activeTab === 'attendees' && (
                <AttendeesTab
                    attendees={attendees}
                    loading={attendeesLoading}
                    total={attendeesTotal}
                    page={attendeePage}
                    totalPages={totalPages}
                    setPage={setAttendeePage}
                    search={attendeeSearch}
                    setSearch={(v) => { setAttendeeSearch(v); setAttendeePage(1); }}
                    selectedAttendees={selectedAttendees}
                    toggleSelectAll={toggleSelectAll}
                    toggleSelectAttendee={toggleSelectAttendee}
                    updatingStatus={updatingStatus}
                    onStatusChange={handleStatusChange}
                    onRemove={handleRemoveAttendee}
                    bulkStatus={bulkStatus}
                    setBulkStatus={setBulkStatus}
                    bulkUpdating={bulkUpdating}
                    onBulkUpdate={handleBulkStatusUpdate}
                    onOpenAddContacts={() => setShowAddContacts(true)}
                />
            )}
            {activeTab === 'photos' && <EventPhotosGrid eventId={eventId} />}
            {activeTab === 'face_review' && <FaceReviewPanel eventId={eventId} />}

            {/* ---- Add Contacts Dialog ---- */}
            <AddContactsDialog
                open={showAddContacts}
                onOpenChange={setShowAddContacts}
                onAdd={handleAddContacts}
                getAuthHeader={getAuthHeader}
            />

            {/* ---- Delete Confirmation Dialog ---- */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent style={{ width: '420px', maxWidth: '95vw', maxHeight: '80vh' }}>
                    <DialogHeader>
                        <DialogTitle>Delete Event</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-stone-600">
                            Are you sure you want to delete <span className="font-semibold">{event.name}</span>? This action cannot be undone.
                            All attendee registrations will also be removed.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                            <Button
                                size="sm"
                                onClick={handleDeleteEvent}
                                disabled={deleting}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                                Delete Event
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ========== OVERVIEW TAB ========== */
function OverviewTab({ event, navigate }) {
    const stats = event.stats || {};

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Registered" value={stats.registered ?? 0} icon={Users} borderColor="border-stone-200" iconColor="text-stone-500" />
                <StatCard label="Confirmed" value={stats.confirmed ?? 0} icon={CheckCircle2} borderColor="border-blue-200" iconColor="text-blue-500" />
                <StatCard label="Attended" value={stats.attended ?? 0} icon={CheckCircle2} borderColor="border-emerald-200" iconColor="text-emerald-500" />
                <StatCard label="No Show" value={stats.no_show ?? 0} icon={XCircle} borderColor="border-red-200" iconColor="text-red-400" />
            </div>

            {/* Event Details Card */}
            <div className="bg-white rounded-lg border border-stone-200 p-5">
                <h3 className="text-sm font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Event Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                        <CalendarDays className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-xs text-stone-400">Date</p>
                            <p className="text-sm text-stone-800 font-medium">
                                {formatDate(event.start_date || event.date)}
                                {event.end_date && event.end_date !== event.start_date && (
                                    <> &mdash; {formatDate(event.end_date)}</>
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start gap-3">
                        <Clock className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-xs text-stone-400">Time</p>
                            <p className="text-sm text-stone-800 font-medium">
                                {formatTime(event.start_date || event.date)}
                                {event.end_date && <> &mdash; {formatTime(event.end_date)}</>}
                                {event.timezone && <span className="text-stone-400 ml-1">({event.timezone})</span>}
                            </p>
                        </div>
                    </div>

                    {event.duration_minutes && (
                        <div className="flex items-start gap-3">
                            <Clock className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-xs text-stone-400">Duration</p>
                                <p className="text-sm text-stone-800 font-medium">{formatDuration(event.duration_minutes)}</p>
                            </div>
                        </div>
                    )}

                    {event.modality && (
                        <div className="flex items-start gap-3">
                            <Globe className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-xs text-stone-400">Modality</p>
                                <p className="text-sm text-stone-800 font-medium capitalize">{event.modality.replace('_', ' ')}</p>
                            </div>
                        </div>
                    )}

                    {/* Location - In Person / Hybrid */}
                    {(event.modality === 'in_person' || event.modality === 'hybrid') && event.location && (
                        <div className="flex items-start gap-3 md:col-span-2">
                            <MapPin className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-xs text-stone-400">Location</p>
                                <p className="text-sm text-stone-800 font-medium">
                                    {typeof event.location === 'string' ? event.location : [event.location.venue_name, event.location.address, event.location.city, event.location.state, event.location.country].filter(Boolean).join(', ') || 'No location set'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Online platform links. For Zoom (or legacy untagged
                        online events) a single Meeting Link; for Velocity
                        three first-class URLs (Event Page / Registration /
                        Webinar Room). Reads online_link from either the
                        nested `location.online_link` or legacy top-level. */}
                    {(event.modality === 'online' || event.modality === 'hybrid') && event.platform === 'velocity' && (
                        <div className="md:col-span-2 space-y-2">
                            <p className="text-xs text-stone-400 font-semibold uppercase tracking-wider">Velocity links</p>
                            {[
                                { key: 'velocity_event_url', label: 'Event Page' },
                                { key: 'velocity_registration_url', label: 'Registration' },
                                { key: 'velocity_webinar_url', label: 'Webinar Room' },
                            ].map(({ key, label }) => event[key] ? (
                                <div key={key} className="flex items-start gap-3">
                                    <Link2 className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-xs text-stone-400">{label}</p>
                                        <a
                                            href={event[key]}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-violet-700 hover:text-violet-900 font-medium inline-flex items-center gap-1 break-all"
                                        >
                                            {event[key]}
                                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                        </a>
                                    </div>
                                </div>
                            ) : null)}
                        </div>
                    )}
                    {(event.modality === 'online' || event.modality === 'hybrid')
                        && event.platform !== 'velocity'
                        && (event.location?.online_link || event.online_link) && (
                        <div className="flex items-start gap-3 md:col-span-2">
                            <Video className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-xs text-stone-400">
                                    {event.platform === 'zoom' ? 'Zoom meeting' : 'Meeting link'}
                                </p>
                                <a
                                    href={event.location?.online_link || event.online_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 break-all"
                                >
                                    {event.location?.online_link || event.online_link}
                                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Fallback: location field shown when no modality set */}
                    {!event.modality && event.location && (
                        <div className="flex items-start gap-3 md:col-span-2">
                            <MapPin className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-xs text-stone-400">Location</p>
                                <p className="text-sm text-stone-800 font-medium">
                                    {typeof event.location === 'string' ? event.location : [event.location.venue_name, event.location.address, event.location.city, event.location.state, event.location.country].filter(Boolean).join(', ') || 'No location set'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Linked Campaigns */}
            {event.campaigns && event.campaigns.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Linked Campaigns</h3>
                    <div className="space-y-2">
                        {event.campaigns.map((campaign) => (
                            <button
                                key={campaign._id || campaign.id}
                                onClick={() => navigate(`/campaigns/${campaign._id || campaign.id}`)}
                                className="w-full flex items-center justify-between px-3 py-2.5 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group"
                            >
                                <span className="text-sm font-medium text-stone-700 group-hover:text-stone-900">{campaign.name}</span>
                                <ExternalLink className="w-3.5 h-3.5 text-stone-400 group-hover:text-stone-600" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Description */}
            {event.description && (
                <div className="bg-white rounded-lg border border-stone-200 p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Description</h3>
                    <p className="text-sm text-stone-600 whitespace-pre-wrap leading-relaxed">{event.description}</p>
                </div>
            )}
        </div>
    );
}

/* ========== ATTENDEES TAB ========== */
function AttendeesTab({
    attendees, loading, total, page, totalPages, setPage,
    search, setSearch, selectedAttendees, toggleSelectAll, toggleSelectAttendee,
    updatingStatus, onStatusChange, onRemove,
    bulkStatus, setBulkStatus, bulkUpdating, onBulkUpdate, onOpenAddContacts,
}) {
    return (
        <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0 w-full sm:w-auto">
                    <div className="relative flex-1 sm:max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input
                            type="text"
                            placeholder="Search attendees..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                        />
                    </div>
                    <span className="text-xs text-stone-400 flex-shrink-0">{total} attendee{total !== 1 ? 's' : ''}</span>
                </div>
                <Button size="sm" onClick={onOpenAddContacts}>
                    <UserPlus className="w-4 h-4 mr-1" /> Add Contacts
                </Button>
            </div>

            {/* Bulk Actions */}
            {selectedAttendees.length > 0 && (
                <div className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5">
                    <span className="text-xs font-medium text-stone-600">{selectedAttendees.length} selected</span>
                    <select
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value)}
                        className="text-xs border border-stone-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                    >
                        <option value="">Update status to...</option>
                        <option value="registered">Registered</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="attended">Attended</option>
                        <option value="no_show">No Show</option>
                    </select>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onBulkUpdate}
                        disabled={!bulkStatus || bulkUpdating}
                    >
                        {bulkUpdating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        Apply
                    </Button>
                    <button onClick={toggleSelectAll} className="text-xs text-stone-400 hover:text-stone-600 ml-auto">
                        Clear selection
                    </button>
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                    </div>
                ) : attendees.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                        <Users className="w-8 h-8 text-stone-300" />
                        <p className="text-sm text-stone-400">No attendees yet</p>
                        <Button size="sm" variant="outline" onClick={onOpenAddContacts} className="mt-2">
                            <UserPlus className="w-4 h-4 mr-1" /> Add Contacts
                        </Button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-stone-100 bg-stone-50/60">
                                    <th className="px-3 py-2.5 text-left w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedAttendees.length === attendees.length && attendees.length > 0}
                                            onChange={toggleSelectAll}
                                            className="rounded border-stone-300"
                                        />
                                    </th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Name</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wider hidden lg:table-cell">Company</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wider hidden lg:table-cell">Title</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Status</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-stone-500 uppercase tracking-wider hidden md:table-cell">Registered</th>
                                    <th className="px-3 py-2.5 text-right text-xs font-medium text-stone-500 uppercase tracking-wider w-16"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {attendees.map((attendee) => {
                                    const id = attendee._id || attendee.id;
                                    const isSelected = selectedAttendees.includes(id);
                                    const contact = attendee.contact || attendee;
                                    const initials = `${(contact.first_name || '?')[0]}${(contact.last_name || '?')[0]}`.toUpperCase();

                                    return (
                                        <tr key={id} className={`hover:bg-stone-50/60 transition-colors ${isSelected ? 'bg-stone-50' : ''}`}>
                                            <td className="px-3 py-2.5">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelectAttendee(id)}
                                                    className="rounded border-stone-300"
                                                />
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-full bg-stone-100 text-stone-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                                                        {initials}
                                                    </div>
                                                    <span className="font-medium text-stone-800 truncate">
                                                        {contact.first_name} {contact.last_name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-stone-500 truncate hidden md:table-cell max-w-[200px]">
                                                {contact.email || '--'}
                                            </td>
                                            <td className="px-3 py-2.5 text-stone-500 truncate hidden lg:table-cell max-w-[150px]">
                                                {contact.account_name || contact.company || '--'}
                                            </td>
                                            <td className="px-3 py-2.5 text-stone-500 truncate hidden lg:table-cell max-w-[150px]">
                                                {contact.title || '--'}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <StatusDropdown
                                                    value={attendee.status || 'registered'}
                                                    onChange={(val) => onStatusChange(id, val)}
                                                    disabled={updatingStatus[id]}
                                                />
                                            </td>
                                            <td className="px-3 py-2.5 text-stone-400 text-xs hidden md:table-cell whitespace-nowrap">
                                                {attendee.registered_at
                                                    ? new Date(attendee.registered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                    : '--'
                                                }
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                <button
                                                    onClick={() => onRemove(id)}
                                                    className="p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                                                    title="Remove attendee"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                    <p className="text-xs text-stone-400">
                        Page {page} of {totalPages} ({total} total)
                    </p>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(Math.max(1, page - 1))}
                            disabled={page <= 1}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ========== COMING SOON TAB ========== */
function ComingSoonTab({ icon: Icon, title }) {
    return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center">
                <Icon className="w-8 h-8 text-stone-400" />
            </div>
            <div className="text-center">
                <h3 className="text-lg font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
                <p className="text-sm text-stone-400 mt-1">Coming Soon in Phase 2</p>
            </div>
        </div>
    );
}
